if (import.meta.env.DEV) {
  // Vite automatically checks which env we are in and will import debug tools accordingly. Please install official Preact Dev tools as a chrome extension.
  import('preact/debug');
}

import { render } from 'preact';
import type { JSX } from 'preact';
import type { WidgetBuildInfo } from './analytics/types';
import { Alert } from './components/base/Alert/Alert';
import { JourneyWidgetTabs } from './components/widget/Widget';
import { WidgetErrorBoundary } from './components/base/WidgetErrorBoundary/WidgetErrorBoundary';
import {
  normalizeWidgetConsentState,
  resolveWidgetConsentState,
  updateWidgetConsentStateAttribute,
  type WidgetConsentState,
} from './consent/consentState';
import { WidgetProviders } from './providers/WidgetProviders';
import { initSentry, setWidgetSentryContext } from './sentry';
import {
  resolveWidgetInitialConfig,
  type WidgetInitialConfig,
} from './widgetConfig';
import { getTenantFooterLinks } from './config/tenantConfig';
import { createStandardWidget } from './widgetElement';

declare const __WIDGET_BUILD_TIMESTAMP__: string;
declare const __WIDGET_BUILD_VERSION__: string;
declare const __WIDGET_BUILD_COMMIT_SHA__: string;

declare global {
  interface Window {
    __PASSENGER_WIDGET_BUILD__?: WidgetBuildInfo;
  }
}

export { WIDGET_READY_DOM_EVENT_NAME } from './widgetElement';

const widgetBuildInfo: WidgetBuildInfo = {
  timestamp: __WIDGET_BUILD_TIMESTAMP__,
  version: __WIDGET_BUILD_VERSION__,
  commitSha: __WIDGET_BUILD_COMMIT_SHA__,
};

if (typeof window !== 'undefined') {
  window.__PASSENGER_WIDGET_BUILD__ = widgetBuildInfo;
}

initSentry(widgetBuildInfo);

const WidgetContent = ({
  hostElement,
  tenant,
  baseUrl,
  initialConfig,
  consentState,
  buildInfo,
}: {
  hostElement: HTMLElement;
  tenant: string;
  baseUrl?: string;
  initialConfig: WidgetInitialConfig;
  consentState: WidgetConsentState;
  buildInfo: WidgetBuildInfo;
}): JSX.Element => {
  if (!tenant) {
    return <Alert variant="error">Error: tenant attribute is required</Alert>;
  }

  const footerLinks = getTenantFooterLinks(tenant);

  return (
    <WidgetErrorBoundary
      hostElement={hostElement}
      tenant={tenant}
      buildInfo={buildInfo}
      initialConfig={initialConfig}
    >
      <WidgetProviders
        hostElement={hostElement}
        tenant={tenant}
        baseUrl={baseUrl}
        initialConfig={initialConfig}
        consentState={consentState}
        buildInfo={buildInfo}
      >
        <JourneyWidgetTabs
          initialTab={initialConfig.initialTab}
          initialOrigin={initialConfig.initialOrigin}
          initialDestination={initialConfig.initialDestination}
          initialDepartureStopId={initialConfig.departureStopId}
          footerLinks={footerLinks}
        />
      </WidgetProviders>
    </WidgetErrorBoundary>
  );
};

function createWidgetStyleSheet(): CSSStyleSheet {
  const styleSheet = new CSSStyleSheet();

  // 1. Grab your global theme styles
  const globalStyles = import.meta.glob('./styles/**/*.css', {
    query: '?inline',
    import: 'default',
    eager: true,
  }) as Record<string, string>;

  const foundationOrder = [
    './styles/firewall.css',
    './styles/reset.css',
    './styles/theme.css',
    './styles/typography.css',
  ];

  const foundationStyles = foundationOrder
    .map((filePath) => globalStyles[filePath])
    .filter(Boolean);

  const additionalGlobalStyles = Object.entries(globalStyles)
    .filter(([filePath]) => !foundationOrder.includes(filePath))
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([, css]) => css);

  // 2. Automatically grab EVERY CSS module string in the project
  const moduleStyles = import.meta.glob('./**/*.module.css', {
    query: '?inline',
    import: 'default',
    eager: true,
  }) as Record<string, string>;

  const orderedModuleStyles = Object.entries(moduleStyles)
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([, css]) => css);

  // 3. Combine them all into one CSS string
  const combinedCss = [
    ...foundationStyles,
    ...additionalGlobalStyles,
    ...orderedModuleStyles,
  ].join('\n');

  styleSheet.replaceSync(combinedCss);

  return styleSheet;
}

const StandardWidget = createStandardWidget({
  styleSheet: createWidgetStyleSheet(),
  buildInfo: widgetBuildInfo,
  renderContent({
    hostElement,
    container,
    tenant,
    baseUrl,
    initialConfig,
    consentState,
    buildInfo,
  }) {
    render(
      <WidgetContent
        hostElement={hostElement}
        tenant={tenant}
        baseUrl={baseUrl}
        initialConfig={initialConfig}
        consentState={consentState}
        buildInfo={buildInfo}
      />,
      container,
    );
  },
  resolveInitialConfig: resolveWidgetInitialConfig,
  resolveConsentState: resolveWidgetConsentState,
  normalizeConsentState: normalizeWidgetConsentState,
  updateConsentStateAttribute: updateWidgetConsentStateAttribute,
  setSentryContext: setWidgetSentryContext,
});

// Define the custom element
if (!customElements.get('passenger-widget')) {
  customElements.define('passenger-widget', StandardWidget);
}
